import React, { useState, useEffect } from 'react';
import { FiClock, FiCheck, FiX, FiCopy, FiExternalLink } from 'react-icons/fi';
import { toast } from 'react-toastify';
import usePaymentPolling from '../hooks/usePaymentPolling';

const PaymentWaitingModal = ({
  invoiceId,
  paymentLink,
  expiresAt,
  memberName,
  amount,
  onSuccess,
  onFailure,
  onClose
}) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [modalState, setModalState] = useState('waiting'); // 'waiting', 'success', 'failed', 'expired'

  // Calculate time remaining
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = Date.now();
      const expiry = new Date(expiresAt).getTime();
      const remaining = Math.max(0, expiry - now);

      setTimeRemaining(remaining);

      if (remaining === 0) {
        setModalState('expired');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  // Poll payment status
  const { status, isPolling } = usePaymentPolling(invoiceId, {
    interval: 5000, // Poll every 5 seconds
    maxDuration: 1320000, // 22 minutes (payment link expires at 20 min)
    onSuccess: () => {
      setModalState('success');
      setTimeout(() => {
        onSuccess?.();
      }, 2000); // Show success message for 2 seconds
    },
    onFailure: (failureReason) => {
      setModalState(failureReason === 'cancelled' ? 'cancelled' : 'failed');
      setTimeout(() => {
        onFailure?.(failureReason);
      }, 3000);
    },
    onTimeout: () => {
      setModalState('expired');
    },
    enabled: modalState === 'waiting'
  });

  const formatTimeRemaining = (ms) => {
    if (!ms) return '--:--';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const copyPaymentLink = () => {
    navigator.clipboard.writeText(paymentLink);
    toast.success('Payment link copied!');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Close button - only show after payment completes */}
        {['success', 'failed', 'expired', 'cancelled'].includes(modalState) && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        )}

        {/* Content based on state */}
        {modalState === 'waiting' && (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Waiting for Payment</h2>
              <p className="text-gray-600">Payment link sent to {memberName}</p>
            </div>

            {/* Timer */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-orange-800">
                  <FiClock className="mr-2" />
                  <span className="font-semibold">Time Remaining</span>
                </div>
                <span className="text-2xl font-bold text-orange-600">
                  {formatTimeRemaining(timeRemaining)}
                </span>
              </div>
              <p className="text-xs text-orange-700 mt-2">
                Payment link expires after 20 minutes
              </p>
            </div>

            {/* Payment Link */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Link (for reference)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={paymentLink}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                />
                <button
                  onClick={copyPaymentLink}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Copy link"
                >
                  <FiCopy />
                </button>
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Open link"
                >
                  <FiExternalLink />
                </a>
              </div>
            </div>

            {/* Status indicator */}
            <div className="text-center text-sm text-gray-500">
              <p>Checking payment status every 5 seconds...</p>
              {status && (
                <p className="mt-2 text-xs">
                  Status: {status.invoice_status}
                  {status.payment_link_status && ` | Link: ${status.payment_link_status}`}
                </p>
              )}
            </div>
          </>
        )}

        {modalState === 'success' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <FiCheck className="text-green-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-4">
              Payment of ₹{amount} received from {memberName}
            </p>
            <p className="text-sm text-gray-500">Redirecting to invoice...</p>
          </div>
        )}

        {modalState === 'failed' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <FiX className="text-red-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-4">
              The payment could not be completed.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {modalState === 'cancelled' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <FiX className="text-gray-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Cancelled</h2>
            <p className="text-gray-600 mb-4">
              The payment was cancelled.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {modalState === 'expired' && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <FiClock className="text-orange-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Link Expired</h2>
            <p className="text-gray-600 mb-4">
              The payment link has expired after 20 minutes.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentWaitingModal;
